import { SplitLayout } from '@components/layout';
import { AuthenticationHeader } from '@components/layout/authentication/AuthenticationHeader';
import { useAuth } from '@hooks/useAuth';
import WarningOutlined from '@mui/icons-material/WarningOutlined';
import { Alert, Typography } from '@mui/material';
import { cookies } from '@utils/cookies';
import { pathToVP } from '@utils/route';
import { isAfter } from 'date-fns';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { LoginFooter } from './LoginFooter';
import { LoginForm } from './LoginForm';

const LoginView = () => {
    const cookieFlag = typeof window !== 'undefined' && cookies.get('keycloak_migration_required') === 1;
    const { isLoggedIn, isUserLoading, migrationDate, user } = useAuth({
        disableUserMigrationVerification: cookieFlag,
    });
    const [searchParams] = useSearchParams();
    const next = searchParams.get('next');
    const [keycloakMigrationAlertEnabled, setKeycloakMigrationAlertEnabled] = useState(cookieFlag);

    useEffect(() => {
        const shouldMigrate = !user?.uses_keycloak && isAfter(new Date(), migrationDate);
        if (!isUserLoading && isLoggedIn && !shouldMigrate) {
            window.location.replace(pathToVP(next ?? '/').toString());
        } else {
            setKeycloakMigrationAlertEnabled(
                typeof window !== 'undefined' && cookies.get('keycloak_migration_required') === 1
            );
        }
    }, [isUserLoading, isLoggedIn, migrationDate, user, next]);

    if (isUserLoading) {
        return (
            <FormattedMessage
                id="common.loading"
                defaultMessage="Loading..."
                description="Loading message for usage while loading data in a page"
            />
        );
    }

    return (
        <>
            <title>Victron Professional - Login</title>
            <SplitLayout>
                <div className="flex flex-col justify-between min-h-full w-full">
                    <div className="flex flex-col">
                        <AuthenticationHeader />
                        {keycloakMigrationAlertEnabled && !isNaN(migrationDate.getTime()) ? (
                            <Alert severity="warning" className="mb-5" icon={<WarningOutlined color="warning-500" />}>
                                <Typography fontFamily="Roboto" fontWeight={500} fontSize={16}>
                                    <FormattedMessage
                                        id="auth.login.header.migrationLogoutAlert"
                                        defaultMessage="You were signed out. Please sign in again to migrate your account to"
                                        description="Alert message when user was logged out due to migration requirements. Used in: /auth/login"
                                    />
                                    <Typography component="span" fontWeight={700} fontSize={14}>
                                        Victron OneID
                                    </Typography>{' '}
                                    .
                                </Typography>
                            </Alert>
                        ) : null}
                        <LoginForm />
                    </div>
                    <LoginFooter />
                </div>
            </SplitLayout>
        </>
    );
};

export { LoginView };
